import { useCallback, useEffect, useRef, useState } from 'react'
import type { NotificationEventEnvelope } from '../api/contracts'
import { getUnreadNotificationsCount } from '../api/notifications/feed'
import { useNotificationServerEvents } from './useNotificationServerEvents'

/** The SSE signal can come before the notification row is committed: one repeat after this delay catches it. */
export const UNREAD_COUNT_SIGNAL_RETRY_MS = 1500
/** Signals within this window after the first one cause one refetch (a burst of notifications, one request). */
export const UNREAD_COUNT_SIGNAL_DEBOUNCE_MS = 300

interface UseUnreadNotificationsCountOptions {
  enabled?: boolean
  /** Called when a refetch triggered by `notification_created` sees a different count. */
  onSignalCountChange?: () => void
  /** Every `notification_created` signal of the one subscription, for listeners such as the toasts. */
  onSignal?: (event: NotificationEventEnvelope) => void
}

export interface UnreadNotificationsCountState {
  count: number
  refresh: () => Promise<number | null>
  /** Optimistic local decrement after a row is marked read; never below zero. */
  decrement: () => void
  /** Local zero after «Прочитать все». */
  reset: () => void
}

interface InitialCountRequest {
  apiBaseUrl: string
  promise: Promise<number>
}

export function useUnreadNotificationsCount(
  apiBaseUrl: string,
  { enabled = true, onSignalCountChange, onSignal }: UseUnreadNotificationsCountOptions = {}
): UnreadNotificationsCountState {
  const [count, setCountState] = useState(0)
  const countRef = useRef(0)
  const requestSeqRef = useRef(0)
  const activeRef = useRef(false)
  /** Changes on every activation and cleanup: an await that sees another value belongs to an old activation. */
  const activationRef = useRef(0)
  /** False until a count arrives in this activation: a local decrement before it would be lost. */
  const isCountKnownRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // StrictMode mounts the effect twice: the second mount reuses the first count request instead of starting another.
  const initialRequestRef = useRef<InitialCountRequest | null>(null)
  const onSignalCountChangeRef = useRef(onSignalCountChange)
  const onSignalRef = useRef(onSignal)

  useEffect(() => {
    onSignalCountChangeRef.current = onSignalCountChange
    onSignalRef.current = onSignal
  }, [onSignalCountChange, onSignal])

  const setCount = useCallback((value: number) => {
    countRef.current = value
    setCountState(value)
  }, [])

  /** The latest count, or `null` when the request failed or a newer one (or a local change) came after it. */
  const fetchLatestCount = useCallback(async (): Promise<number | null> => {
    const seq = ++requestSeqRef.current
    try {
      const value = await getUnreadNotificationsCount(apiBaseUrl)
      return seq === requestSeqRef.current ? value : null
    } catch {
      // The bell keeps the last known count; the next signal, reconnect or tab switch refetches it.
      return null
    }
  }, [apiBaseUrl])

  const refresh = useCallback(async (): Promise<number | null> => {
    const value = await fetchLatestCount()
    if (value !== null) {
      isCountKnownRef.current = true
      setCount(value)
    }
    return value
  }, [fetchLatestCount, setCount])

  const clearTimers = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  const handleSignal = useCallback(async () => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    const activation = activationRef.current
    const before = countRef.current
    const after = await refresh()
    if (!activeRef.current || activation !== activationRef.current) {
      return
    }
    if (after !== null && after !== before) {
      onSignalCountChangeRef.current?.()
      return
    }
    if (retryTimerRef.current !== null) {
      return
    }
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null
      const beforeRetry = countRef.current
      void refresh().then((retried) => {
        if (activeRef.current && activation === activationRef.current && retried !== null && retried !== beforeRetry) {
          onSignalCountChangeRef.current?.()
        }
      })
    }, UNREAD_COUNT_SIGNAL_RETRY_MS)
  }, [refresh])

  const scheduleSignal = useCallback(() => {
    if (!activeRef.current || debounceTimerRef.current !== null) {
      return
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null
      void handleSignal()
    }, UNREAD_COUNT_SIGNAL_DEBOUNCE_MS)
  }, [handleSignal])

  useEffect(() => {
    if (!enabled) {
      return
    }
    activeRef.current = true
    activationRef.current += 1
    isCountKnownRef.current = false
    const seq = ++requestSeqRef.current
    let initial = initialRequestRef.current
    if (!initial || initial.apiBaseUrl !== apiBaseUrl) {
      const request: InitialCountRequest = { apiBaseUrl, promise: getUnreadNotificationsCount(apiBaseUrl) }
      initial = request
      initialRequestRef.current = request
      request.promise.then(
        () => { if (initialRequestRef.current === request) initialRequestRef.current = null },
        () => { if (initialRequestRef.current === request) initialRequestRef.current = null }
      )
    }
    initial.promise.then(
      (value) => {
        if (seq === requestSeqRef.current) {
          isCountKnownRef.current = true
          setCount(value)
        }
      },
      () => {
        // The bell keeps zero; the next signal, reconnect or tab switch refetches it.
      }
    )
    return () => {
      activeRef.current = false
      activationRef.current += 1
      requestSeqRef.current += 1
      clearTimers()
    }
  }, [enabled, apiBaseUrl, setCount, clearTimers])

  useEffect(() => {
    if (!enabled) {
      return
    }
    function handleVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        void refresh()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, refresh])

  useNotificationServerEvents(apiBaseUrl, {
    enabled,
    onMessage: (event) => {
      onSignalRef.current?.(event)
      scheduleSignal()
    },
    onReconnect: () => {
      void refresh()
    }
  })

  // A local change wins over a count request already in flight: its answer may predate the change.
  const decrement = useCallback(() => {
    if (!isCountKnownRef.current) {
      // Nothing to decrement from yet: a fresh count already reflects the read row. The refetch supersedes
      // the initial request, so a failed refetch is repeated once instead of leaving the bell at zero.
      const activation = activationRef.current
      void refresh().then((value) => {
        if (value === null && !isCountKnownRef.current && activeRef.current && activation === activationRef.current) {
          void refresh()
        }
      })
      return
    }
    requestSeqRef.current += 1
    setCount(Math.max(0, countRef.current - 1))
  }, [refresh, setCount])

  const reset = useCallback(() => {
    requestSeqRef.current += 1
    isCountKnownRef.current = true
    setCount(0)
  }, [setCount])

  return { count, refresh, decrement, reset }
}
